import { Text as MantineText, Button, Flex, NumberInput, TextInput, Badge } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { ChangeEvent, useCallback, useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getCurrentUser } from "@/services/auth";
import usePendingNotasStore from "../../stores/usePendingNotasStore";

interface Fase {
  id: string;
  titulo: string;
  cor: string;
  checked: boolean;
}

interface MyState {
  nota: string;
  justificativa: string;
}

const NotaItem = ({
  pergunta,
  fase,
  grupo
}: {
  pergunta: TPergunta | null | undefined;
  fase: Fase;
  grupo: TGruposDiagnostico;
}) => {
  const addPendingNota = usePendingNotasStore((state) => state.addPendingNota);
  const pendingNotas = usePendingNotasStore((state) => state.pendingNotas);

  const originalValues = { nota: "", justificativa: "" };
  const currentUserId = getCurrentUser()?.uid;
  const { idEmpresa } = useParams<{ idEmpresa: string }>();

  // Chave única para esta nota nova: combinação de idResposta e fase
  const notaKey = useMemo(() => {
    return `${pergunta?.id}-${fase.id}`;
  }, [pergunta?.id, fase.id]);

  // Verifica se há uma nota pendente
  const pendingNota = useMemo(() => {
    return pendingNotas.get(notaKey);
  }, [pendingNotas, notaKey]);

  // Se há nota pendente, usa os valores pendentes
  const initialValues =
    pendingNota && pendingNota.type === "nova"
      ? { nota: pendingNota.nota, justificativa: pendingNota.justificativa }
      : mockValues;

  const [values, setValues] = useState<MyState>(initialValues);
  const hasChanges = values.nota !== originalValues.nota || values.justificativa !== originalValues.justificativa;
  const isPending = pendingNota !== undefined;

  // Sincroniza estado quando pendingNota mudar
  useEffect(() => {
    if (pendingNota && pendingNota.type === "nova") {
      setValues({ nota: pendingNota.nota, justificativa: pendingNota.justificativa });
    }
  }, [pendingNota]);

  const handleUpdate = useCallback(() => {
    if (!pergunta?.id || !currentUserId || !idEmpresa) return;

    // Adiciona ao estado pendente ao invés de salvar imediatamente
    addPendingNota(notaKey, {
      type: "nova",
      nota: values.nota,
      justificativa: values.justificativa,
      fase: fase.id,
      idResposta: pergunta.id,
      respondente: currentUserId,
      idEmpresaNota: idEmpresa,
      grupo: grupo.id
    });

    showNotification({
      message: `Nota marcada para salvar. Clique em "Fechar Avaliação e Atualizar Notas" para salvar todas.`,
      color: "blue",
      autoClose: 3000
    });
  }, [addPendingNota, notaKey, values, fase.id, pergunta?.id, currentUserId, idEmpresa, grupo.id]);

  return (
    <Flex gap="md" direction="row" justify="left" align="center">
      <MantineText>{fase && fase?.titulo}</MantineText>
      <NumberInput style={{ width: 80 }} placeholder="Nota" onChange={onChangeNumber(setValues)} value={values.nota} />
      <TextInput placeholder="Justificativa da nota" onChange={onChangeText(setValues)} value={values.justificativa} />
      <Flex gap="sm" direction="row" justify="flex-end" align="center">
        <Button disabled={values.nota === "" || !values.justificativa || !hasChanges} onClick={handleUpdate}>
          {isPending ? "Atualizar Pendente" : "Marcar para Salvar"}
        </Button>
        {isPending && (
          <Badge color="yellow" variant="light">
            Pendente
          </Badge>
        )}
      </Flex>
    </Flex>
  );
};

export default NotaItem;

// prettier-ignore
const onChangeNumber = (setValues: React.Dispatch<React.SetStateAction<MyState>>) => {
  return (value: string) => {
    setValues(values => ({ ...values, nota: value }))
  }
}

// prettier-ignore
const onChangeText = (setValues: React.Dispatch<React.SetStateAction<MyState>>) => {
  return (event: ChangeEvent<HTMLInputElement>) => {
    setValues(values => ({ ...values, justificativa: event.target.value }))
  }
};

// prettier-ignore
const mockValues = {
  nota: "",
  justificativa: ""
}
